import {
  defaultEditEmployeesValues,
  useEditEmployeeDrawerForm,
  EmployeeDrawerForm,
  EditEmployeeForm,
} from '@pages/employees/components';
import {
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerClose,
  DrawerTitle,
  Button,
  Drawer,
  toast,
} from '@ui/components';
import { AddEmployeeRequest, ResponseErrorDto } from '@interfaces/models';
import { useEmployees } from '@pages/employees/hooks/use-employees';
import { useQueryClient } from '@tanstack/react-query';
import { sanitizePhoneNumber } from '@helpers/utils';
import { useTranslation } from 'react-i18next';
import { Cancel } from '@ui/components/icons';

interface AddEmployeeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddEmployeeDrawer = (props: AddEmployeeDrawerProps) => {
  const { isOpen, onClose } = props;

  const { t } = useTranslation();
  const { form } = useEditEmployeeDrawerForm();
  const queryClient = useQueryClient();

  const { mutateAddEmployee, isAddEmployeePending } = useEmployees({
    onAddSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees-table'] });
      onClose();
      toast.success(t('EMPLOYEES.ADD_EMPLOYEE_SUCCESS'));
    },
    onAddError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const handleSave = (employee: EditEmployeeForm) => {
    const data = {
      ...(employee as AddEmployeeRequest),
      phoneNumber: sanitizePhoneNumber(employee.phoneNumber),
    };
    mutateAddEmployee(data);
  };

  return (
    <Drawer
      onClose={() => {
        form.reset(defaultEditEmployeesValues);
        onClose();
      }}
      onOpenChange={onClose}
      open={isOpen}
      handleOnly
    >
      <DrawerContent className="gap-0">
        <DrawerHeader className="flex">
          <DrawerTitle>{t('EMPLOYEES.ADD_EMPLOYEE_DRAWER_TITLE')}</DrawerTitle>
          <div className="flex ml-auto">
            <DrawerClose asChild>
              <Button variant="ghost" type="button">
                <Cancel />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <div className="p-4 overflow-y-auto flex-1">
          <EmployeeDrawerForm selectedEmployee={null} form={form} />
        </div>
        <DrawerFooter>
          <Button
            onClick={form.handleSubmit(handleSave)}
            isLoading={isAddEmployeePending}
            disabled={isAddEmployeePending}
            size="large"
            wide
          >
            {t('EMPLOYEES.DRAWER_SAVE_CHANGES')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
